import { tr } from '@/lib/i18n-core';
import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useIsFetching, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { ApiError, get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { DataTable, Dialog, PageHeader, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { useRealtimeInvalidate } from '@/lib/realtime';

const PAGE = 50;
const REFRESH_GAP_MS = 60_000;
const LIST_KEY = 'critical-results';

interface PatientRef {
  id: string;
  mrn: string;
  fullName: string;
  phone: string | null;
}

interface CriticalTiming {
  notifiedAt: string | null;
  notDelivered?: boolean;
  escalatedAt: string | null;
  referringDoctorName: string | null;
  referringDoctorPhone: string | null;
  minutesOpen: number;
  escalationMinutes: number;
  overdue: boolean;
}

interface LabCritical extends CriticalTiming {
  resultId: string;
  orderItemId: string;
  orderId: string;
  orderNumber: string;
  patient: PatientRef;
  test: { id: string; code: string; name: string; unit: string | null };
  value: string;
  unit: string | null;
  flag: string | null;
  enteredAt: string;
}

interface RadiologyCritical extends CriticalTiming {
  studyId: string;
  studyNumber: string;
  patient: PatientRef;
  studyType: { id: string; code: string; name: string };
  finding: string | null;
  reportedAt: string | null;
}

interface CriticalPage<T> {
  data: T[];
  nextCursor: string | null;
}

interface AcknowledgeTarget {
  kind: 'lab' | 'radiology';
  id: string;
  patientName: string;
  summary: string;
}

function minutesText(value: number): string {
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  if (hours < 24) return minutes ? `${hours} h ${minutes} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest ? `${days} d ${rest} h` : `${days} d`;
}

function useRefreshOnReturn(refresh: () => void): () => void {
  const lastRefresh = useRef(Date.now());
  const latest = useRef(refresh);
  latest.current = refresh;

  useEffect(() => {
    const onChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefresh.current < REFRESH_GAP_MS) return;
      lastRefresh.current = Date.now();
      latest.current();
    };
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  return () => {
    lastRefresh.current = Date.now();
    latest.current();
  };
}

function useCriticalList<T>(kind: 'lab' | 'radiology', fetchPage: (search: string) => Promise<CriticalPage<T>>) {
  return useInfiniteQuery({
    queryKey: [LIST_KEY, kind],
    initialPageParam: '',
    queryFn: ({ pageParam }) => fetchPage(qs({ limit: PAGE, cursor: pageParam })),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function CriticalValuesPage() {
  const { can, hasModule } = useAuth();
  const queryClient = useQueryClient();
  const fetching = useIsFetching({ queryKey: [LIST_KEY] });
  const showLab = can('lab.critical.acknowledge', 'lab.result.verify', 'lab.master.manage');
  const showRadiology =
    hasModule('radiology') && can('radiology.critical.acknowledge', 'radiology.report.verify', 'radiology.worklist');
  const [tab, setTab] = useState(showLab ? 'lab' : 'radiology');
  const [acknowledging, setAcknowledging] = useState<AcknowledgeTarget | null>(null);

  const refreshNow = useRefreshOnReturn(() => void queryClient.invalidateQueries({ queryKey: [LIST_KEY] }));
  useRealtimeInvalidate([...(showLab ? ['lab-critical'] : []), ...(showRadiology ? ['radiology-critical'] : [])], null, [[LIST_KEY]]);

  if (!showLab && !showRadiology) {
    return (
      <>
        <PageHeader title={tr('lab', 'critical_results')} />
        <Alert variant="warning">{tr('lab', 'your_role_cannot_see_critical_results')}</Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={tr('lab', 'critical_results')}
        description={tr('lab', 'critical_values_and_findings_that_nobody_has')}
        actions={
          <Button variant="outline" onClick={refreshNow} loading={fetching > 0}>
            {fetching === 0 && <RefreshCw className="h-4 w-4" />} {tr('lab', 'refresh')}
          </Button>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          ...(showLab ? [{ id: 'lab', label: tr('lab', 'laboratory') }] : []),
          ...(showRadiology ? [{ id: 'radiology', label: tr('lab', 'radiology') }] : []),
        ]}
      />

      {tab === 'lab' && showLab && (
        <LabCriticalTab
          canAcknowledge={can('lab.critical.acknowledge', 'lab.result.verify')}
          onAcknowledge={setAcknowledging}
        />
      )}
      {tab === 'radiology' && showRadiology && (
        <RadiologyCriticalTab
          canAcknowledge={can('radiology.critical.acknowledge', 'radiology.report.verify')}
          onAcknowledge={setAcknowledging}
        />
      )}

      {acknowledging && (
        <AcknowledgeDialog
          target={acknowledging}
          onClose={() => setAcknowledging(null)}
          onDone={() => void queryClient.invalidateQueries({ queryKey: [LIST_KEY] })}
        />
      )}
    </>
  );
}

function OpenFor({ row }: { row: CriticalTiming }) {
  return (
    <div>
      <p className={row.overdue ? 'font-semibold text-destructive' : 'font-medium'}>{minutesText(row.minutesOpen)}</p>
      <p className="text-xs text-muted-foreground">{tr('lab', 'sms_escalation_after')} {minutesText(row.escalationMinutes)}</p>
    </div>
  );
}

function Urgency({ row }: { row: CriticalTiming }) {
  return (
    <div className="flex flex-wrap gap-1">
      {row.notDelivered && (
        <Badge variant="destructive" title={tr('lab', 'no_doctor_escalation_number_or_patient_phone')}>
          {tr('lab', 'not_delivered')}
        </Badge>
      )}
      {row.overdue && <Badge variant="destructive">{tr('lab', 'overdue')}</Badge>}
      {row.escalatedAt && (
        <Badge variant="destructive" title={tr('lab', 'escalation_sent_x', { escalatedAt: dateTime(row.escalatedAt) })}>
          {tr('lab', 'escalated')}
        </Badge>
      )}
      {!row.overdue && !row.escalatedAt && <Badge variant="warning">{tr('lab', 'waiting')}</Badge>}
    </div>
  );
}

function PhoneLink({ phone }: { phone: string | null }) {
  if (!phone) return <span className="text-xs text-muted-foreground">{tr('lab', 'no_phone_on_file')}</span>;
  return (
    <a className="text-xs underline" href={`tel:${phone}`}>
      {phone}
    </a>
  );
}

function PatientCell({ patient }: { patient: PatientRef }) {
  return (
    <div>
      <p className="font-medium">{patient.fullName}</p>
      <p className="text-xs text-muted-foreground">{patient.mrn}</p>
      <PhoneLink phone={patient.phone} />
    </div>
  );
}

function DoctorCell({ row }: { row: CriticalTiming }) {
  return (
    <div>
      <p>{row.referringDoctorName ?? tr('lab', 'no_referring_doctor')}</p>
      <PhoneLink phone={row.referringDoctorPhone} />
    </div>
  );
}

function loadMoreFooter(query: { hasNextPage: boolean; isFetchingNextPage: boolean; fetchNextPage: () => unknown }) {
  if (!query.hasNextPage) return undefined;
  return (
    <div className="flex w-full justify-center">
      <Button variant="outline" size="sm" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
        {tr('lab', 'load_more')}
      </Button>
    </div>
  );
}

function LabCriticalTab({
  canAcknowledge,
  onAcknowledge,
}: {
  canAcknowledge: boolean;
  onAcknowledge: (target: AcknowledgeTarget) => void;
}) {
  const query = useCriticalList<LabCritical>('lab', (search) =>
    get<CriticalPage<LabCritical>>(`/lab/results/critical/unacknowledged${search}`),
  );
  const rows = query.data?.pages.flatMap((page) => page.data);

  return (
    <>
      <DataTable
        rows={rows}
        loading={query.isLoading} refreshing={query.isFetching}
        error={query.error}
        rowKey={(row) => row.resultId}
        empty={tr('lab', 'no_critical_laboratory_result_is_waiting_to_be')}
        footer={loadMoreFooter(query)}
        columns={[
          { header: tr('lab', 'open_for'), sort: (row) => row.minutesOpen, cell: (row) => <OpenFor row={row} /> },
          { header: tr('lab', 'status'), cell: (row) => <Urgency row={row} /> },
          { header: tr('lab', 'patient'), sort: (row) => row.patient.fullName, cell: (row) => <PatientCell patient={row.patient} /> },
          {
            header: tr('lab', 'result'),
            sort: (row) => row.test.name,
            cell: (row) => (
              <div>
                <p>{row.test.name}</p>
                <p className="text-sm">
                  <span className="font-semibold">
                    {row.value} {row.unit ?? row.test.unit ?? ''}
                  </span>
                  {row.flag && (
                    <Badge variant="destructive" className="ms-2">
                      {humanize(row.flag)}
                    </Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {tr('lab', 'order')} {row.orderNumber} {tr('lab', 'entered')} {dateTime(row.enteredAt)}
                </p>
              </div>
            ),
          },
          { header: tr('lab', 'referring_doctor'), sort: (row) => row.referringDoctorName, cell: (row) => <DoctorCell row={row} /> },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              canAcknowledge ? (
                <Button
                  size="sm"
                  onClick={() =>
                    onAcknowledge({
                      kind: 'lab',
                      id: row.resultId,
                      patientName: row.patient.fullName,
                      summary: `${row.test.name}: ${row.value} ${row.unit ?? row.test.unit ?? ''}`.trim(),
                    })
                  }
                >
                  {tr('lab', 'acknowledge')}
                </Button>
              ) : null,
          },
        ]}
      />
    </>
  );
}

function RadiologyCriticalTab({
  canAcknowledge,
  onAcknowledge,
}: {
  canAcknowledge: boolean;
  onAcknowledge: (target: AcknowledgeTarget) => void;
}) {
  const query = useCriticalList<RadiologyCritical>('radiology', (search) =>
    get<CriticalPage<RadiologyCritical>>(`/radiology/critical-findings/unacknowledged${search}`),
  );
  const rows = query.data?.pages.flatMap((page) => page.data);

  return (
    <>
      <DataTable
        rows={rows}
        loading={query.isLoading} refreshing={query.isFetching}
        error={query.error}
        rowKey={(row) => row.studyId}
        empty={tr('lab', 'no_critical_imaging_finding_is_waiting_to_be')}
        footer={loadMoreFooter(query)}
        columns={[
          { header: tr('lab', 'open_for'), sort: (row) => row.minutesOpen, cell: (row) => <OpenFor row={row} /> },
          { header: tr('lab', 'status'), cell: (row) => <Urgency row={row} /> },
          { header: tr('lab', 'patient'), sort: (row) => row.patient.fullName, cell: (row) => <PatientCell patient={row.patient} /> },
          {
            header: tr('lab', 'finding'),
            sort: (row) => row.studyType.name,
            cell: (row) => (
              <div>
                <p>{row.studyType.name}</p>
                <p className="text-sm font-semibold">{row.finding ?? tr('lab', 'critical_finding_flagged')}</p>
                <p className="text-xs text-muted-foreground">
                  {tr('lab', 'study')} {row.studyNumber} {tr('lab', 'reported')} {dateTime(row.reportedAt)}
                </p>
              </div>
            ),
          },
          { header: tr('lab', 'referring_doctor'), sort: (row) => row.referringDoctorName, cell: (row) => <DoctorCell row={row} /> },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              canAcknowledge ? (
                <Button
                  size="sm"
                  onClick={() =>
                    onAcknowledge({
                      kind: 'radiology',
                      id: row.studyId,
                      patientName: row.patient.fullName,
                      summary: `${row.studyType.name}: ${row.finding ?? 'critical finding flagged'}`,
                    })
                  }
                >
                  {tr('lab', 'acknowledge')}
                </Button>
              ) : null,
          },
        ]}
      />
    </>
  );
}

function AcknowledgeDialog({
  target,
  onClose,
  onDone,
}: {
  target: AcknowledgeTarget;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [toldName, setToldName] = useState('');
  const [readBackNote, setReadBackNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const told = toldName.trim();
  const note = readBackNote.trim();
  const valid = told.length >= 2 && note.length >= 3;

  const save = useMutation({
    mutationFn: () => {
      const body = { toldName: told, readBackNote: note };
      return target.kind === 'lab'
        ? post(`/lab/results/${target.id}/acknowledge`, body)
        : post(`/radiology/studies/${target.id}/acknowledge`, body);
    },
    onSuccess: () => {
      toast.success(tr('lab', 'critical_result_acknowledged'), tr('lab', 'x_was_told_about_x', { told: told, patientName: target.patientName }));
      onDone();
      onClose();
    },
    onError: (caught) => {
      if (caught instanceof ApiError && caught.status === 409) {
        toast.warning(tr('lab', 'already_acknowledged'), caught.message);
        onDone();
        onClose();
        return;
      }
      setError(describeError(caught));
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('lab', 'acknowledge_a_critical_result')}
      description={`${target.patientName} · ${target.summary}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('lab', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            disabled={!valid}
          >
            {tr('lab', 'record_acknowledgement')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={tr('lab', 'who_was_told')} required hint={tr('lab', 'the_doctor_or_nurse_who_took_the_result_by_name')}>
          <Input value={toldName} maxLength={120} onChange={(e) => setToldName(e.target.value)} autoFocus />
        </Field>
        <Field label={tr('lab', 'read_back_note')} required hint={tr('lab', 'what_they_repeated_back_to_confirm_they_heard')}>
          <Textarea value={readBackNote} maxLength={500} rows={3} onChange={(e) => setReadBackNote(e.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
